import { ReactComponent as UserListIcon } from '../../static/icons/user-list.svg';
import { ReactComponent as SettingIcon } from '../../static/icons/settings-01.svg';
import { ReactComponent as SparklesIcon } from '../../static/icons/shopping-cart-check-01.svg';
import { ReactComponent as SignalFullIcon } from '../../static/icons/signal-full-01.svg';
import { ReactComponent as BirthdayCakeIcon } from '../../static/icons/birthday-cake.svg';

/**
 * Sidebar navigation items configuration
 */
export const sidebarItems = [
  {
    label: 'Overview',
    key: 'overview',
    id: 'overview-section',
    icon: UserListIcon,
  },
  {
    label: 'Core Findings',
    key: 'core-findings',
    id: 'core-findings-section',
    icon: SettingIcon,
  },
  {
    label: 'Recommendations',
    key: 'recommendations',
    id: 'recommendations-section',
    icon: SparklesIcon,
  },
  {
    label: 'Behaviour',
    key: 'behaviour',
    id: 'behaviour-section',
    icon: SignalFullIcon,
  },
  {
    label: 'Demographics',
    key: 'demographics',
    id: 'demographics-section',
    icon: BirthdayCakeIcon,
  },
];

/**
 * Extract audience name and label from details
 * @param {Object} details - Persona details object
 * @returns {Object} { audienceName, audienceLabel }
 */
export const getAudienceNameAndLabel = (details, loaderData) => {
  let audienceName = '';
  let audienceLabel = '';

  [audienceName, audienceLabel] = details.outline.label.split(':');
  audienceName = audienceName || '';
  audienceLabel = audienceLabel || '';

  if (loaderData?.customName !== details?.outline?.label) {
    audienceName = loaderData?.customName;
  }

  return {
    audienceName,
    audienceLabel,
  };
};

/**
 * Create a persona snapshot from demographics data
 * @param {Object} details - Persona details object
 * @returns {Object} Persona snapshot with age, gender, income, education, locations
 */
export const getPersonaSnapshot = details => {
  const { age, gender, income, education } = details?.demographicsV3 || {};

  // Find the most dominant age group
  const dominantAge =
    age && age.length > 0
      ? age.reduce(
          (max, item) => (item.weight > max.weight ? item : max),
          age[0]
        ).bucket
      : null;

  // Find the most dominant gender(s) - calculate percentages
  let genderData = null;
  if (gender && gender.length > 0) {
    const maleGender = gender.find(item => item.label === 'Male');
    const femaleGender = gender.find(item => item.label === 'Female');
    genderData = {
      male: maleGender ? Number((maleGender.weight * 100).toFixed(1)) : 0,
      female: femaleGender ? Number((femaleGender.weight * 100).toFixed(1)) : 0,
    };
  }

  // Find the most dominant income group
  const dominantIncome =
    income && income.length > 0
      ? income.reduce(
          (max, item) => (item.weight > max.weight ? item : max),
          income[0]
        ).bucket
      : null;

  // Find the most dominant education level
  const dominantEducation =
    education && education.length > 0
      ? education.reduce(
          (max, item) => (item.weight > max.weight ? item : max),
          education[0]
        ).label
      : null;

  return {
    age: dominantAge,
    gender: genderData,
    income: dominantIncome,
    education: dominantEducation,
    locations: getTopLocations(details?.promptDetails?.top_locations) || [],
  };
};

function getTopLocations(locations) {
  if (!locations || locations.length === 0) {
    return [];
  }

  if (typeof locations[0] === 'string') {
    return locations.slice(0, 3);
  }

  if (typeof locations[0] === 'object') {
    return [...locations]
      .sort((a, b) => b.weight - a.weight)
      .slice(0, 3)
      .map(item => item.city);
  }
}

/**
 * Normalize anchors data for radar chart
 * @param {Array} anchorsMap - Array of anchor objects
 * @returns {Array} Normalized anchors data with percentage scores
 */
export const normalizeAnchorsData = anchorsMap => {
  if (!Array.isArray(anchorsMap) || anchorsMap.length === 0) {
    return [];
  }

  return anchorsMap.map(item => ({
    ...item,
    normalized_score: Math.round((item.normalized_score || 0) * 100),
  }));
};

/**
 * Transform demographics age data for chart
 * @param {Array} ageData - Array of age objects with bucket and weight
 * @returns {Object} Object with bucket as key and weight as value
 */
export const transformAgeData = ageData => {
  if (!Array.isArray(ageData) || ageData.length === 0) {
    return {};
  }

  return Object.fromEntries(
    ageData.map(({ bucket, weight }) => [bucket || 'Unknown', weight || 0])
  );
};

/**
 * Transform demographics ethnicity data for chart
 * @param {Array} ethnicityData - Array of ethnicity objects with label and weight
 * @returns {Object} Object with ethnicity data transformed for chart
 */
export const transformEthnicityData = ethnicityData => {
  if (!Array.isArray(ethnicityData) || ethnicityData.length === 0) {
    return {};
  }

  return {
    ethnicity: Object.fromEntries(
      ethnicityData.map(({ label, weight }) => [
        label || 'Unknown',
        (weight || 0) * 100,
      ])
    ),
  };
};

/**
 * Transform demographics education data for chart
 * @param {Array} educationData - Array of education objects with label and weight
 * @returns {Object} Object with educationLevel as key and weight as value
 */
export const transformEducationData = educationData => {
  if (!Array.isArray(educationData) || educationData.length === 0) {
    return {};
  }

  return {
    educationLevel: Object.fromEntries(
      educationData.map(({ label, weight }) => [
        label || 'Unknown',
        weight || 0,
      ])
    ),
  };
};

/**
 * Transform demographics income data for chart
 * @param {Array} incomeData - Array of income objects with bucket and weight
 * @returns {Object} Object with income as key and weight as value
 */
export const transformIncomeData = incomeData => {
  if (!Array.isArray(incomeData) || incomeData.length === 0) {
    return {};
  }

  return {
    income: Object.fromEntries(
      incomeData.map(({ bucket, weight }) => [bucket || 'Unknown', weight || 0])
    ),
  };
};
